import { Box } from '@chakra-ui/react';
import { PortableText } from '@portabletext/react';
import {
  Banner,
  BenefitCards,
  Benefits,
  CategoriesIntro,
  ChartBlock,
  ComparisonTable,
  Facts,
  FeatureList,
  HaloShowcase,
  ImageWithCallouts,
  KeyIngredients,
  MediaBlock,
  MitochondrialDecline,
  MitopureTransformation,
  Onwards5050,
  PressMarquee,
  Quote,
  QuoteCarousel,
  TextBlock,
  TextMediaSplit,
} from '@time-line/components/blocks';
import { Grid } from '@time-line/components/common';
import { ScientificResearchDrawer } from '@time-line/components/drawers';
import { ProductIconsList } from 'components/product/ProductIconsList';
import { MitopureSolution } from 'components/science/MitopureSolution';
import { ABPaths } from 'components/sections/ABPaths';
import { BeforeAfter } from 'components/sections/BeforeAfter';
import { BlogCardCarousel } from 'components/sections/BlogCardCarousel';
import { CertificationsBanner } from 'components/sections/CertificationsBanner';
import { Timeline } from 'components/sections/CompanyTimeline/Timeline';
import { FaqsBlock } from 'components/sections/Faqs';
import { FaqsJsonLd } from 'components/sections/Faqs/FaqsJsonLd';
import { HowToEarnPoints } from 'components/sections/HowToEarnPoints';
import { ListicleItem } from 'components/sections/ListicleItem';
import { MitopureElaboration } from 'components/sections/MitopureElaboration';
import { PartnerPerksCarousel } from 'components/sections/PartnerPerksCarousel';
import { PodcastBanner } from 'components/sections/PodcastBanner';
import { ProductBlock } from 'components/sections/ProductBlock';
import { ProductBundle } from 'components/sections/ProductBundle';
import { ProductCarouselWrapper } from 'components/sections/ProductCardCarousel/ProductCarouselWrapper';
import { ProductComparison } from 'components/sections/ProductComparison';
import { ReviewsCarousel } from 'components/sections/ReviewsCarousel';
import { SignupBanner } from 'components/sections/SignupBanner';
import { SkincareFormatPicker } from 'components/sections/SkincareFormatPicker';
import { SocialMediaCarousel } from 'components/sections/SocialMediaCarousel';
import { SupplementPicker } from 'components/sections/SupplementPicker';
import { Team } from 'components/sections/Team';
import { TestimonialsCarousel } from 'components/sections/TestimonialsCarousel';
import { VibeGallery } from 'components/sections/VibeGallery';
import { WallOfLove } from 'components/sections/WallOfLove';
import { useIsSkincare } from 'hooks/useIsSkincare';
import { useScriptCondition } from 'hooks/useScriptManager';
import { SCIENCE, SKINCARE_SCIENCE, STUDIES } from 'lib/constants/routes';
import type { ComponentType } from 'react';
import { Fragment, useEffect, useMemo } from 'react';
import type {
  BeforeAfter as BeforeAfterProps,
  CategoriesIntro as CategoriesIntroProps,
  Certifications,
  ChartBlock as ChartBlockProps,
  ComparisonTable as ComparisonTableProps,
  IconsList,
  ListicleItem as ListicleItemProps,
  MitochondrialDecline as MitochondrialDeclineProps,
  MitopureSolution as MitopureSolutionProps,
  PartnerPerksCarousel as PartnerPerksCarouselProps,
  PodcastBanner as PodcastBannerProps,
  ProductBundle as ProductBundleProps,
  ProductCarousel as ProductCarouselProps,
  Team as TeamProps,
  TestimonialGroup,
  WallOfLove as WallOfLoveProps,
} from 'types/cms/blocks';
import type { PageModule } from 'types/cms/PageModule';

import { PageFootnoteLink } from '../PageFootnote';
import { RichTextResolver } from '../RichTextResolver';
import type { BlockRules } from './utils';
import { DEFAULT_BLOCK_RULES } from './utils';

export { SectionHeaderContainer } from './containers';

interface PageModuleResolverProps {
  blocks: PageModule[];
  blockRules?: BlockRules;
  pageTitle?: string;
  container?: ComponentType<any>;
}

export function PageModuleResolver({
  blocks,
  pageTitle,
  blockRules: _blockRules = {},
  container: Container = Fragment,
}: PageModuleResolverProps) {
  const isSkincare = useIsSkincare();
  const setCondition = useScriptCondition();
  const blockRules = {
    ...DEFAULT_BLOCK_RULES,
    ..._blockRules,
  };

  const hasVibeGallery = useMemo(
    () => blocks.some((block) => block._type === 'timeline_vibeGallery'),
    [blocks],
  );

  useEffect(() => {
    setCondition('isVibeGalleryOnPage', hasVibeGallery);
    return () => setCondition('isVibeGalleryOnPage', false);
  }, [setCondition, hasVibeGallery]);

  return (
    /* eslint-disable react/no-unstable-nested-components */
    <Container>
      <FaqsJsonLd blocks={blocks} />
      <PortableText
        value={blocks}
        components={{
          types: {
            timeline_productBlock: ({ value }) => <ProductBlock {...value} />,
            timeline_quote: ({ value }) => <Quote {...value} />,
            timeline_quoteCarousel: ({ value }) => <QuoteCarousel {...value} />,
            timeline_facts: ({ value }) => (
              <Facts
                {...value}
                RichTextResolver={RichTextResolver}
                drawerInner={
                  <ScientificResearchDrawer
                    cta={{ href: STUDIES, title: 'Our Studies' }}
                  />
                }
              />
            ),
            timeline_mitopureElaboration: () => <MitopureElaboration />,
            timeline_mitopureSolution: ({
              value,
            }: {
              value: MitopureSolutionProps;
            }) => <MitopureSolution {...value} />,
            iconsList: ({ value }: { value: IconsList }) => {
              const pyMap = {
                sm: 'm',
                lg: 'xl',
              } as const;

              return (
                <Grid
                  sx={{
                    '> div': {
                      py: pyMap[value.variant],
                    },
                  }}
                  _first={{
                    '> div': {
                      borderBottomWidth: 1,
                      borderColor: 'blackAlpha.250',
                    },
                  }}
                  role="group"
                >
                  <Box gridColumn="main">
                    <ProductIconsList {...value} />
                  </Box>
                </Grid>
              );
            },
            timeline_certifications: ({ value }: { value: Certifications }) => (
              <CertificationsBanner {...value} />
            ),
            textMediaRef: ({ value }) => <TextMediaSplit {...value} />,
            timeline_banner: ({ value }) => <Banner {...value} />,
            timeline_signupBanner: ({ value }) => <SignupBanner {...value} />,
            timeline_benefitCards: ({ value }) => (
              <BenefitCards {...value} RichTextResolver={RichTextResolver} />
            ),
            timeline_abPaths: ({ value }) => <ABPaths {...value} />,
            productCarousel: ({ value }: { value: ProductCarouselProps }) => (
              <ProductCarouselWrapper productCarousel={value} />
            ),
            timeline_comparisonTable: ({
              value,
            }: {
              value: ComparisonTableProps;
            }) => (
              <ComparisonTable
                {...value}
                PageFootnoteLink={PageFootnoteLink}
                richTextResolver={RichTextResolver}
              />
            ),
            timeline_testimonialGroup: ({
              value,
            }: {
              value: TestimonialGroup;
            }) => <TestimonialsCarousel {...value} />,
            timeline_faq: ({ value }) => <FaqsBlock {...value} />,
            timeline_podcast: ({ value }: { value: PodcastBannerProps }) => (
              <PodcastBanner {...value} />
            ),
            beforeAfter: ({ value }: { value: BeforeAfterProps }) => (
              <BeforeAfter {...value} />
            ),
            imageWithCallouts: ({ value }) => (
              <ImageWithCallouts
                {...value}
                RichTextResolver={RichTextResolver}
              />
            ),
            timeline_categoriesIntro: ({
              value,
            }: {
              value: CategoriesIntroProps;
            }) => (
              <CategoriesIntro {...value} RichTextResolver={RichTextResolver} />
            ),
            timeline_chartBlock: ({ value }: { value: ChartBlockProps }) => (
              <ChartBlock
                {...value}
                isSkincare={isSkincare}
                RichTextResolver={RichTextResolver}
              />
            ),
            mitopureTransformation: ({ value }) => (
              <MitopureTransformation
                {...value}
                RichTextResolver={RichTextResolver}
              />
            ),
            pressQuotes: ({ value }) => <PressMarquee {...value} />,
            productBundle: ({ value }: { value: ProductBundleProps }) => (
              <ProductBundle {...value} />
            ),
            listicleItem: ({ value }: { value: ListicleItemProps }) => (
              <ListicleItem {...value} />
            ),
            timeline_team: ({ value }: { value: TeamProps }) => (
              <Team {...value} pageTitle={pageTitle} />
            ),
            reviewsCarousel: ({ value }) => <ReviewsCarousel {...value} />,
            wallOfLove: ({ value }: { value: WallOfLoveProps }) => (
              <WallOfLove {...value} />
            ),
            blogCarousel: ({ value }) => <BlogCardCarousel {...value} />,
            productComparison: ({ value }) => <ProductComparison {...value} />,
            timeline_mitochondrialDecline: ({
              value,
            }: {
              value: MitochondrialDeclineProps;
            }) => (
              <MitochondrialDecline
                {...value}
                RichTextResolver={RichTextResolver}
              />
            ),
            timeline_mediaBlock: ({ value }) => <MediaBlock {...value} />,
            textBlock: ({ value, index }) => (
              <TextBlock
                {...value}
                isDividerVisible={blockRules.isDividerVisible(index, blocks)}
                richTextResolver={RichTextResolver}
              />
            ),
            timeline_productIngredients: ({ value }) => (
              <KeyIngredients
                {...value}
                scienceCta={{
                  href: isSkincare ? SKINCARE_SCIENCE : SCIENCE,
                  title: 'Discover our science',
                }}
              />
            ),
            timeline_companyTimeline: ({ value }) => <Timeline {...value} />,
            timeline_onwards5050: ({ value }) => <Onwards5050 {...value} />,
            socialMediaCarousel: ({ value }) => (
              <SocialMediaCarousel {...value} />
            ),
            featureList: ({ value, index }) => (
              <FeatureList
                {...value}
                isDividerVisible={blockRules.isDividerVisible(index, blocks)}
              />
            ),
            benefitsV2: ({ value }) => (
              <Benefits RichTextResolver={RichTextResolver} {...value} />
            ),
            howToEarnPointsBlock: ({ value }) => <HowToEarnPoints {...value} />,
            partnerPerksCarousel: ({
              value,
            }: {
              value: PartnerPerksCarouselProps;
            }) => (
              <PartnerPerksCarousel
                {...value}
                RichTextResolver={RichTextResolver}
              />
            ),
            timeline_vibeGallery: ({ value }) => <VibeGallery {...value} />,
            timeline_supplementPicker: ({ value }) => (
              <SupplementPicker {...value} />
            ),
            timeline_skincareFormatPicker: ({ value }) => (
              <SkincareFormatPicker {...value} />
            ),
            timeline_haloShowcase: ({ value }) => (
              <HaloShowcase {...value} RichTextResolver={RichTextResolver} />
            ),
            // TODO(TIME-645): drop once the TIME-531 migration has run on every dataset.
            timeline_scienceTeaser: ({ value }) => (
              <HaloShowcase {...value} RichTextResolver={RichTextResolver} />
            ),
          },
        }}
      />
    </Container>
  );
}
